/**
 * No scroll event for this long means the momentum scroll has stopped. iOS WebViews
 * deliver momentum scroll events in bursts with gaps of 250ms+, so keep it generous.
 */
const MOMENTUM_IDLE_MS = 500;

type TouchScrollCallbacks = {
  /** A finger touched the screen; a drag and then a momentum scroll may follow. */
  onStart: () => void;
  /** The finger lifted and any momentum scroll has stopped. */
  onSettle: () => void;
};

/**
 * Tracks a touch scroll interaction: from finger down, through the drag, until the
 * momentum scroll after the finger lifts has stopped. Never starts without touch
 * input. Returns a function that stops tracking.
 */
export function trackTouchScroll({ onStart, onSettle }: TouchScrollCallbacks): () => void {
  let active = false;
  let fingersDown = 0;
  let idleTimer: ReturnType<typeof setTimeout> | undefined;

  const clearIdleTimer = () => {
    if (idleTimer !== undefined) clearTimeout(idleTimer);
    idleTimer = undefined;
  };
  const settle = () => {
    clearIdleTimer();
    if (!active) return;
    active = false;
    onSettle();
  };
  const settleLater = () => {
    clearIdleTimer();
    idleTimer = setTimeout(settle, MOMENTUM_IDLE_MS);
  };

  const onTouchStart = (e: TouchEvent) => {
    fingersDown = e.touches.length;
    clearIdleTimer();
    if (active) return;
    active = true;
    onStart();
  };
  const onTouchEnd = (e: TouchEvent) => {
    fingersDown = e.touches.length;
    if (fingersDown === 0) settleLater();
  };
  const onScroll = () => {
    if (active && fingersDown === 0) settleLater();
  };

  const options = { capture: true, passive: true };
  document.addEventListener("touchstart", onTouchStart, options);
  document.addEventListener("touchend", onTouchEnd, options);
  document.addEventListener("touchcancel", onTouchEnd, options);
  document.addEventListener("scroll", onScroll, options);

  return () => {
    settle();
    document.removeEventListener("touchstart", onTouchStart, options);
    document.removeEventListener("touchend", onTouchEnd, options);
    document.removeEventListener("touchcancel", onTouchEnd, options);
    document.removeEventListener("scroll", onScroll, options);
  };
}
